/**
 * Billing Hooks
 *
 * React Query hooks for:
 * - Billing summary (plan, usage, renewal)
 * - Usage breakdown
 * - Premium add-ons
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export interface UsageMetric {
  current: number
  limit: number
  percentage: number
  warning_threshold: number
  is_near_limit: boolean
}

export interface BillingSummary {
  plan: 'trial' | 'pro' | 'enterprise'
  plan_display_name: string
  status: 'active' | 'pending' | 'suspended' | 'cancelled'
  started_at: string | null
  renewal_date: string | null
  trial_ends_at: string | null
  days_remaining: number | null
  max_profiles: number
  max_api_keys: number
  rate_limit_per_minute: number
  profiles_used: number
  api_keys_used: number
  active_addons: string[]
}

export interface UsageBreakdown {
  profiles: UsageMetric
  api_keys: UsageMetric
  rate_limit: UsageMetric
  api_requests_this_month: number
  api_requests_limit: number | null
}

export interface FeatureAddon {
  key: string
  name: string
  description: string
  category: string
  is_available: boolean
  is_active: boolean
  pricing_type: 'contact' | 'flat' | 'per_seat'
  price: number | null
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  summary: (tenantId: string) => ['billing-summary', tenantId],
  usage: (tenantId: string) => ['billing-usage', tenantId],
  addons: (tenantId: string) => ['billing-addons', tenantId],
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Get billing summary
 */
export function useBillingSummary(tenantId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.summary(tenantId),
    queryFn: async () => {
      const { data } = await api.get<BillingSummary>(
        `/tenants/${tenantId}/billing/summary`
      )
      return data
    },
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Get detailed usage breakdown
 */
export function useUsageBreakdown(tenantId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.usage(tenantId),
    queryFn: async () => {
      const { data } = await api.get<UsageBreakdown>(
        `/tenants/${tenantId}/billing/usage`
      )
      return data
    },
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Get available premium add-ons
 */
export function useFeatureAddons(tenantId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.addons(tenantId),
    queryFn: async () => {
      const { data } = await api.get<FeatureAddon[]>(
        `/tenants/${tenantId}/billing/addons`
      )
      return data
    },
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}

/**
 * Request a premium add-on
 */
export function useRequestAddon(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ addonKey, notes }: { addonKey: string; notes?: string }) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        `/tenants/${tenantId}/billing/addons/${addonKey}/request`,
        { notes }
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.addons(tenantId) })
    },
  })
}

// =============================================================================
// HELPER HOOKS
// =============================================================================

/**
 * Quick access to plan tier badge color
 */
export function usePlanBadgeColor(plan: string) {
  const colors: Record<string, string> = {
    trial: 'bg-amber-100 text-amber-800',
    pro: 'bg-green-100 text-green-800',
    enterprise: 'bg-purple-100 text-purple-800',
  }
  return colors[plan] || 'bg-gray-100 text-gray-800'
}
